import {inject, Injectable} from '@angular/core';
import {UserAuthenticationService} from './user-authentication.service';
import {HelperService} from './helper.service';
import {firstValueFrom, interval, Subscription, tap} from 'rxjs';
import {DokumentModels} from '../models/dokument-models';
import {LocalStorageService} from '../local-storage.service';
import {SWISS_DOCUMENT_SYNC_QUEUE} from '../models/local-storage-names';
import {DokumentDataService} from './dokument-data.service';
import {MessageBoxService} from './message-box.service';
import {v4 as uuidv4} from 'uuid';
import {HttpErrorResponse} from '@angular/common/http';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import DokumentInfo = DokumentModels.DokumentInfo;
import DocumentInfoWithFile = DokumentModels.DocumentInfoWithFile;

@Injectable({
    providedIn: 'root',
})
export class DocumentManagementService {
    private userAuthService = inject(UserAuthenticationService);
    private helperService = inject(HelperService);
    private localStorageService = inject(LocalStorageService);
    private messageBoxService = inject(MessageBoxService);
    private docService = inject(DokumentDataService);
    private isOpenDialog = false;

    constructor() {
        this.helperService.addPeriodicalCheck(this.check.bind(this));
        this.helperService.getOnlineStateChanged().pipe(takeUntilDestroyed(),
            tap(async (onlineState) => {
                if (onlineState && !!this.userAuthService.getCurrentAuthenticatedUser()) {
                    this.check();
                }
            })).subscribe();
    }

    public static async downLoadFile(docInfo: DocumentInfoWithFile): Promise<void> {
        const downloadLink = document.createElement('a');
        // https://stackoverflow.com/questions/60072027/html5-video-source-as-locally-stored-blob-not-working-anymore/60075517#60075517
        // TODO 2025-10-02 https://swissjira.atlassian.net/browse/EN-3974 consider saving Files differently IndexDB cause IOS issues
        downloadLink.href = window.URL.createObjectURL(new Blob([await docInfo.file.arrayBuffer()], {type: 'application/pdf'}));
        if (docInfo.fileName) {
            downloadLink.setAttribute('download', docInfo.fileName);
        }
        document.body.appendChild(downloadLink);
        downloadLink.click();

        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(downloadLink.href);
    }

    async addDocumentToSyncQueue(docInfo: DokumentInfo, pdf: Blob): Promise<void> {
        const doc = new DocumentInfoWithFile(docInfo, pdf);
        await this.localStorageService.add(doc.documentID, doc, SWISS_DOCUMENT_SYNC_QUEUE);
    }

    async getDocument(docID: string, global = false): Promise<DocumentInfoWithFile | null> {
        return await this.localStorageService.get<DocumentInfoWithFile | null>(docID, SWISS_DOCUMENT_SYNC_QUEUE, global, null);
    }

    async remove(docID: string): Promise<string> {
        return await this.localStorageService.delete(docID, SWISS_DOCUMENT_SYNC_QUEUE, false);
    }

    async getAllDocumentIDs(): Promise<string[]> {
        return await this.localStorageService.getAllKeys(SWISS_DOCUMENT_SYNC_QUEUE);
    }

    async syncDocuments(documentsKeys: string[] = []): Promise<{ successCount: number, errorCount: number }> {
        if (documentsKeys.length < 1) {
            documentsKeys = await this.getAllDocumentIDs();
        }
        const promises: Promise<string>[] = [];
        for (const key of documentsKeys) {
            const doc = await this.getDocument(key, true);
            if (doc) {
                const promise = this.saveDocument(doc, 0);
                promises.push(promise);
            }
        }
        const results = await Promise.allSettled(promises);
        let errorCount = 0;
        let successCount = 0;
        for (const result of results) {
            if (result.status === 'fulfilled') {
                successCount++;
                await this.remove(result.value);
            } else {
                this.messageBoxService.showErrorByLanguageKey('MAIN_APP_SETTINGS.SYNC_DOCUMENTS.SYNC_RESULT.SINGLE_SYNC_ERROR.text', 'MAIN_APP_SETTINGS.SYNC_DOCUMENTS.SYNC_RESULT.SINGLE_SYNC_ERROR.title', [result.reason]);
                errorCount++;
            }
        }
        if (errorCount === 0) {
            return {successCount: documentsKeys.length, errorCount: 0};
        } else {
            return {successCount, errorCount};
        }
    }

    public async saveDocument(document: DocumentInfoWithFile, depth: number): Promise<string> {
        try {
            const pdf = new File([document.file], document.fileName, {type: 'application/pdf'});
            await firstValueFrom(this.docService.uploadDokumentWithInfo(pdf, document));
            await this.docService.replaceDokumentInDB(pdf, false);
            return document.documentID;
        } catch (ex) {
            if (ex instanceof HttpErrorResponse) {
                return await this.handleSaveError(ex, document, depth + 1);
            }
            throw Error(document.fileName);
        }
    }

    private async handleSaveError(err: HttpErrorResponse, docInfo: DocumentInfoWithFile, depth: number): Promise<string> {
        if (depth > 1) {
            const res = await this.messageBoxService.askYesNoQuestion('MAIN_APP_SETTINGS.SYNC_DOCUMENTS.SYNC_RESULT.DOWNLOAD_COPY.text', 'MAIN_APP_SETTINGS.SYNC_DOCUMENTS.SYNC_RESULT.DOWNLOAD_COPY.title', [docInfo.fileName]);
            if (res) {
                await DocumentManagementService.downLoadFile(docInfo);
                return docInfo.documentID;
            }
        }

        const copyInfo = Object.assign({}, docInfo);
        copyInfo.documentID = uuidv4();
        copyInfo.fileName = copyInfo.fileName.substring(0, copyInfo.fileName.lastIndexOf('.')) + '_KOPIE' + copyInfo.fileName.substring(copyInfo.fileName.lastIndexOf('.'));
        if (err.status === 409) {
            // trying to save the copy after a file conflict was detected
            copyInfo.lastModified = new Date(err.error.timestamp);
            await this.addDocumentToSyncQueue(copyInfo, copyInfo.file);
            await this.remove(docInfo.documentID);
            if (await this.saveDocument(copyInfo, depth + 1)) {
                this.messageBoxService.showInfoByLangagueKey('MAIN_APP_SETTINGS.SYNC_DOCUMENTS.SYNC_RESULT.WITH_ERRORS.CREATE_COPY.text', 'MAIN_APP_SETTINGS.SYNC_DOCUMENTS.SYNC_RESULT.WITH_ERRORS.CREATE_COPY.title', [copyInfo.fileName]);
                return copyInfo.documentID;
            } else {
                // this will lead into download prompt case of depth > 1
                return await this.handleSaveError(err, docInfo, depth + 1);
            }
        } else {
            // in case the error is something like timeout where we are unsure what to really do to handle it
            // this will lead into download prompt case of depth > 1
            return await this.handleSaveError(err, docInfo, depth + 1);
        }
    }

    private check(): Subscription {
        const seconds = interval(60000).subscribe(async () => {
            if (this.helperService.isWebServiceAvailable() && !!this.userAuthService.getCurrentAuthenticatedUser()) {
                const documents = await this.getAllDocumentIDs();
                if (documents.length > 0 && !this.isOpenDialog) {
                    this.isOpenDialog = true;
                    const result = await this.messageBoxService.askYesNoQuestion('MAIN_APP_SETTINGS.SYNC_DOCUMENTS.SYNC_AVAILABLE.text', 'MAIN_APP_SETTINGS.SYNC_DOCUMENTS.SYNC_AVAILABLE.title', ['' + documents.length]);
                    this.isOpenDialog = false;
                    if (result) {
                        const result2 = await this.syncDocuments();
                        if (result2.errorCount === 0) {
                            this.messageBoxService.showSuccessByLanguageKey('MAIN_APP_SETTINGS.SYNC_DOCUMENTS.SYNC_RESULT.ALL_SUCCESS.text', 'MAIN_APP_SETTINGS.SYNC_DOCUMENTS.SYNC_RESULT.ALL_SUCCESS.title');
                        } else if (result2.successCount > 0) {
                            this.messageBoxService.showSuccessByLanguageKey('MAIN_APP_SETTINGS.SYNC_DOCUMENTS.SYNC_RESULT.WITH_ERRORS.text', 'MAIN_APP_SETTINGS.SYNC_DOCUMENTS.SYNC_RESULT.WITH_ERRORS.title', ['' + result2.successCount, '' + result2.errorCount]);
                        } else {
                            this.messageBoxService.showErrorByLanguageKey('MAIN_APP_SETTINGS.SYNC_DOCUMENTS.SYNC_RESULT.ALL_ERRORS.text', 'MAIN_APP_SETTINGS.SYNC_DOCUMENTS.SYNC_RESULT.ALL_ERRORS.title');
                        }
                    }
                }
            }
        });
        return seconds;
    }
}
